import { ScrollArea } from '@/components/ui/scroll-area';
import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { useAuth } from '@/components/auth/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Edit, Trash2, GripVertical, ArrowLeft, MessageSquare, Users, FileUp, ChevronUp, ChevronDown, Database, ChevronRight, Telescope, Settings, ShieldAlert, CreditCard, Mail, Globe, Rocket, TicketPercent, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Header } from '@/components/Header';
import { UserManagement } from '@/components/admin/UserManagement';
import { LensProperties } from '@/components/admin/LensProperties';
import { SecurityAlerts } from '@/components/admin/SecurityAlerts';
import { Base44Connection } from '@/components/admin/Base44Connection';
import { ExternalBase44Apps } from '@/components/admin/ExternalBase44Apps';
import { ProviderHealthChecks } from '@/components/admin/ProviderHealthChecks';
import { CreditRequests } from '@/components/admin/CreditRequests';
import { InviteManagement } from '@/components/admin/InviteManagement';
import { GuestIpUsage } from '@/components/admin/GuestIpUsage';
import { WizardPromos } from '@/components/admin/WizardPromos';
import { CopyServiceAdmin } from '@/components/admin/CopyServiceAdmin';
import { TemplateCatalogAdmin } from '@/components/admin/TemplateCatalogAdmin';
import { BetaAccess } from '@/components/admin/BetaAccess';
import OperatingSystems from './OperatingSystems';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { fetchAttributeContent, fetchAttributeSummaries, fetchAttributesForExport, fetchLensesForExport, type AttributeContent, type AttributeSummary } from '@/lib/lensAttributes';
import { canonicalAdminTab, withAdminTab, type AdminSection } from '@/lib/adminTabs';

// --- Types ---

interface ThinkingMethod {
  id: string;
  name: string;
  author: string;
  author_email: string | null;
  bio: string;
  icon_url: string | null;
  order_index: number;
  produces: string[] | null;
  score: number;
  status: string;
}

interface UploadedLens {
  id: string;
  name: string;
  author: string;
  bio: string | null;
  icon_path: string | null;
  produces: string[] | null;
  score: number | null;
  user_id: string;
}

const CrossedWrenches = ({ className, ...props }: React.SVGProps<SVGSVGElement>) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    <path
      d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
      transform="matrix(-1 0 0 1 24 0)"
    />
  </svg>
);

const NAV_ITEMS: { key: AdminSection; label: string; icon: React.ElementType; group?: string }[] = [
  { key: 'invites', label: 'Invites', icon: Mail },
  { key: 'beta-access', label: 'Beta Access', icon: Users },
  { key: 'wizard-promos', label: 'Wizard Promos', icon: TicketPercent },
  { key: 'credits', label: 'Credit Requests', icon: CreditCard },
  { key: 'users', label: 'User Management', icon: Users },
  { key: 'guests', label: 'Guest IPs', icon: Globe },
  { key: 'security', label: 'Security Alerts', icon: ShieldAlert },
  { key: 'base44', label: 'Base44 Connection', icon: CrossedWrenches },
  { key: 'base44-external-apps', label: 'External Apps', icon: CrossedWrenches },
  { key: 'base44-health', label: 'Provider Watchdog', icon: CrossedWrenches },
  { key: 'CopyService', label: 'CopyService', icon: Settings },
  { key: 'apps', label: 'Apps', icon: Rocket },
  { key: 'Templates', label: 'Templates', icon: Rocket },
  { key: 'conversations', label: 'Conversations', icon: MessageSquare },
  { key: 'lenses', label: 'Edit Expert Lens', icon: Edit, group: 'Lenses' },
  { key: 'properties', label: 'Properties', icon: Database, group: 'Lenses' },
];

// --- Component ---

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const activeSection = canonicalAdminTab(searchParams.get('tab'));

  useEffect(() => {
    if (searchParams.get('tab') !== activeSection) {
      setSearchParams(withAdminTab(searchParams, activeSection), { replace: true });
    }
  }, [activeSection, searchParams, setSearchParams]);
  const [thinkingMethods, setThinkingMethods] = useState<ThinkingMethod[]>([]);
  const [lensSearch, setLensSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingMethod, setEditingMethod] = useState<ThinkingMethod | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSelectLensDialogOpen, setIsSelectLensDialogOpen] = useState(false);
  const [unassociatedLenses, setUnassociatedLenses] = useState<UploadedLens[]>([]);
  const [loadingLenses, setLoadingLenses] = useState(false);
  const [attributes, setAttributes] = useState<Map<string, AttributeSummary[]>>(new Map());
  const [expandedLens, setExpandedLens] = useState<string | null>(null);
  const [selectedAttr, setSelectedAttr] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] = useState<AttributeContent | null>(null);
  const [attributeError, setAttributeError] = useState<string | null>(null);
  const [propertiesLensId, setPropertiesLensId] = useState<string | null>(null);
  const [previewIconUrl, setPreviewIconUrl] = useState<string | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    author: '',
    author_email: '',
    bio: '',
    produces: '',
    score: '4.5',
    status: 'draft',
    position: '',
    icon_file: null as File | null
  });

  const checkAdminStatus = useCallback(async () => {
    if (!user) return;
    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('user_id', user.id)
      .maybeSingle();
    if (!profile?.is_admin) {
      navigate('/');
      toast({ title: "Access Denied", description: "You don't have admin privileges", variant: "destructive" });
      return;
    }
    setIsAdmin(true);
  }, [navigate, toast, user]);

  const fetchThinkingMethods = useCallback(async () => {
    const { data, error } = await supabase.from('thinking_methods').select('*').order('order_index');
    if (error) {
      toast({ title: "Error", description: "Failed to fetch experts", variant: "destructive" });
    } else {
      setThinkingMethods(data || []);
    }
    setLoading(false);
  }, [toast]);

  const fetchAttributeNames = useCallback(async () => {
    try {
    const data = await fetchAttributeSummaries();
    const grouped = new Map<string, AttributeSummary[]>();
    for (const attr of data) {
      const list = grouped.get(attr.thinking_method_id) || [];
      list.push(attr);
      grouped.set(attr.thinking_method_id, list);
    }
    setAttributes(grouped);
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to fetch attribute names', variant: 'destructive' });
    }
  }, [toast]);

  useEffect(() => {
    if (!expandedLens || !selectedAttr) {
      setSelectedContent(null);
      setAttributeError(null);
      return;
    }
    let current = true;
    setSelectedContent(null);
    setAttributeError(null);
    fetchAttributeContent(expandedLens, selectedAttr).then(
      (content) => { if (current) setSelectedContent(content); },
      (error) => { if (current) setAttributeError(error instanceof Error ? error.message : 'Failed to fetch attribute'); }
    );
    return () => { current = false; };
  }, [expandedLens, selectedAttr]);

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      
      // Fetch all lenses and attributes
      const lenses = await fetchLensesForExport();
      
      const attrs = await fetchAttributesForExport();
      
      // Group attributes by thinking_method_id
      const attrsMap = new Map<string, AttributeContent[]>();
      for (const attr of attrs || []) {
        const list = attrsMap.get(attr.thinking_method_id) || [];
        list.push(attr);
        attrsMap.set(attr.thinking_method_id, list);
      }
      
      // Fields to extract from JSON_PROPERTIES
      const requiredFields = ['short_description', 'explain', 'industry', 'header', 'teaser', 'skills'];
      
      // Excel has a 32,767 character limit per cell
      const EXCEL_CELL_LIMIT = 32767;
      let truncationOccurred = false;
      
      const truncateText = (text: string): string => {
        if (text.length <= EXCEL_CELL_LIMIT) return text;
        truncationOccurred = true;
        const truncationMessage = '\n\n[... TRUNCATED - Text exceeds Excel cell limit ...]';
        const maxLength = EXCEL_CELL_LIMIT - truncationMessage.length;
        return text.substring(0, maxLength) + truncationMessage;
      };
      
      // Prepare data for Excel
      const excelData = (lenses || []).map(lens => {
        const lensAttrs = attrsMap.get(lens.id) || [];
        
        // Find the JSON_PROPERTIES attribute
        const jsonPropsAttr = lensAttrs.find(a => a.attribute_name === 'JSON_PROPERTIES');
        let jsonProps: Record<string, string> = {};
        
        if (jsonPropsAttr) {
          try {
            jsonProps = JSON.parse(jsonPropsAttr.attribute_value);
          } catch (e) {
            console.error(`Failed to parse JSON_PROPERTIES for lens ${lens.name}:`, e);
          }
        }
        
        // Create row with explicit column order
        const row: Record<string, string> = {};
        
        // Fixed columns first
        row['Book Name'] = lens.name || '';
        row['Author'] = lens.author || '';
        
        // Add fields from JSON_PROPERTIES
        requiredFields.forEach(field => {
          const value = jsonProps[field];
          row[field] = value ? truncateText(String(value)) : '';
        });
        
        return row;
      });
      
      // Check if JSON_PROPERTIES was found
      const hasJsonProps = (lenses || []).some(lens => {
        const attrs = attrsMap.get(lens.id) || [];
        return attrs.some(a => a.attribute_name === 'JSON_PROPERTIES');
      });
      
      if (!hasJsonProps) {
        console.warn('No JSON_PROPERTIES found in database!');
        toast({
          title: "Warning",
          description: "No JSON_PROPERTIES found. Only Book Name and Author will be exported.",
          variant: "destructive"
        });
      }
      
      // Create workbook and worksheet with explicit column order
      const columnHeaders = ['Book Name', 'Author', ...requiredFields];
      
      // Create worksheet with explicit headers
      const worksheet = XLSX.utils.json_to_sheet(excelData, { header: columnHeaders });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Lenses');
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `lenses_export_${timestamp}.xlsx`;
      
      // Download file
      XLSX.writeFile(workbook, filename);
      
      toast({
        title: "Export Successful",
        description: `Downloaded ${filename} with ${excelData.length} lenses${truncationOccurred ? '. Some long text fields were truncated due to Excel limits.' : ''}`
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Failed to export lenses",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Wait until auth has finished loading before deciding to redirect
    if (authLoading) return;
    if (!user) {
      // Preserve current path + query (e.g. /admin?tab=credits) so we return after login
      const redirectTo = `${window.location.pathname}${window.location.search}`;
      navigate(`/auth?redirect=${encodeURIComponent(redirectTo)}`);
      return;
    }
    checkAdminStatus();
    fetchThinkingMethods();
  }, [user, authLoading, navigate, checkAdminStatus, fetchThinkingMethods]);

  useEffect(() => {
    if (isAdmin && activeSection === 'lenses') fetchAttributeNames();
  }, [isAdmin, activeSection, fetchAttributeNames]);

  const uploadIcon = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const { error: uploadError } = await supabase.storage.from('thinking-method-icons').upload(fileName, file, {
      contentType: file.type,
      upsert: true
    });
    if (uploadError) {
      console.error('Upload error:', uploadError);
      toast({ title: "Upload Error", description: uploadError.message, variant: "destructive" });
      return null;
    }
    const { data: { publicUrl } } = supabase.storage.from('thinking-method-icons').getPublicUrl(fileName);
    return publicUrl;
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<File> => {
    const image = new Image();
    await new Promise<void>((resolve) => {
      image.onload = () => resolve();
      image.src = imageSrc;
    });
    const maxSize = 512;
    const scale = Math.min(1, maxSize / Math.max(pixelCrop.width, pixelCrop.height));
    const outputWidth = Math.round(pixelCrop.width * scale);
    const outputHeight = Math.round(pixelCrop.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, outputWidth, outputHeight);
    const sx = Math.max(0, pixelCrop.x);
    const sy = Math.max(0, pixelCrop.y);
    const sw = Math.min(pixelCrop.width, image.naturalWidth - sx);
    const sh = Math.min(pixelCrop.height, image.naturalHeight - sy);
    const dx = (sx - pixelCrop.x) * scale;
    const dy = (sy - pixelCrop.y) * scale;
    ctx.drawImage(image, sx, sy, sw, sh, dx, dy, sw * scale, sh * scale);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(new File([blob!], 'cropped-icon.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.85);
    });
  };

  const openCropperFromUrl = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
      setCropImageSrc(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (err) {
      toast({ title: "Error", description: "Could not load image for adjustment", variant: "destructive" });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;
    const croppedFile = await getCroppedImg(cropImageSrc, croppedAreaPixels);
    setFormData(prev => ({ ...prev, icon_file: croppedFile }));
    setCropImageSrc(null);
  };

  const handleCropCancel = () => {
    setCropImageSrc(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      let iconFile = formData.icon_file;
      if (cropImageSrc && croppedAreaPixels) {
        iconFile = await getCroppedImg(cropImageSrc, croppedAreaPixels);
        setCropImageSrc(null);
      }
      let iconUrl = editingMethod?.icon_url || previewIconUrl || null;
      if (iconFile) {
        const uploadedUrl = await uploadIcon(iconFile);
        if (uploadedUrl) {
          iconUrl = uploadedUrl;
        } else {
          toast({ title: "Warning", description: "Icon upload failed, keeping existing icon", variant: "destructive" });
        }
      }
      const methodData = {
        name: formData.name, author: formData.author, author_email: formData.author_email || null,
        bio: formData.bio, icon_url: iconUrl,
        order_index: editingMethod ? editingMethod.order_index : thinkingMethods.length,
        produces: formData.produces ? formData.produces.split(',').map(p => p.trim()).filter(Boolean) : [],
        score: parseFloat(formData.score), status: formData.status
      };
      let error;
      if (editingMethod) {
        ({ error } = await supabase.from('thinking_methods').update(methodData).eq('id', editingMethod.id));
      } else {
        ({ error } = await supabase.from('thinking_methods').insert([methodData]));
      }
      if (error) throw error;
      toast({ title: "Success", description: `Expert Lens ${editingMethod ? 'updated' : 'created'} successfully` });
      setIsDialogOpen(false);
      resetForm();
      fetchThinkingMethods();
    } catch (error: unknown) {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed to save expert lens", variant: "destructive" });
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this expert lens?')) return;
    
    try {
      const { error } = await supabase.from('thinking_methods').delete().eq('id', id);
      
      if (error) {
        console.error('Delete error:', error);
        
        // Check for specific error types
        if (error.code === '23503') {
          toast({ 
            title: "Cannot Delete", 
            description: "This lens is being used by conversations or other data. Please remove those references first.", 
            variant: "destructive" 
          });
        } else {
          toast({ 
            title: "Error", 
            description: error.message || "Failed to delete expert lens", 
            variant: "destructive" 
          });
        }
      } else {
        toast({ title: "Success", description: "Expert Lens deleted successfully" });
        fetchThinkingMethods();
      }
    } catch (err) {
      console.error('Unexpected delete error:', err);
      toast({ 
        title: "Error", 
        description: err instanceof Error ? err.message : "Failed to delete expert lens", 
        variant: "destructive" 
      });
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const cur = thinkingMethods[index], prev = thinkingMethods[index - 1];
    const { error: e1 } = await supabase.from('thinking_methods').update({ order_index: prev.order_index }).eq('id', cur.id);
    const { error: e2 } = await supabase.from('thinking_methods').update({ order_index: cur.order_index }).eq('id', prev.id);
    if (e1 || e2) { toast({ title: "Error", description: "Failed to reorder", variant: "destructive" }); }
    else { fetchThinkingMethods(); }
  };

  const handleMoveDown = async (index: number) => {
    if (index === thinkingMethods.length - 1) return;
    const cur = thinkingMethods[index], next = thinkingMethods[index + 1];
    const { error: e1 } = await supabase.from('thinking_methods').update({ order_index: next.order_index }).eq('id', cur.id);
    const { error: e2 } = await supabase.from('thinking_methods').update({ order_index: cur.order_index }).eq('id', next.id);
    if (e1 || e2) { toast({ title: "Error", description: "Failed to reorder", variant: "destructive" }); }
    else { fetchThinkingMethods(); }
  };

  const handleMoveToPosition = async () => {
    if (!editingMethod) return;

    const requestedPosition = Number(formData.position);
    if (!Number.isInteger(requestedPosition) || requestedPosition < 1 || requestedPosition > thinkingMethods.length) {
      toast({
        title: "Choose a valid position",
        description: `Enter a whole number from 1 to ${thinkingMethods.length}.`,
        variant: "destructive"
      });
      return;
    }

    const currentIndex = thinkingMethods.findIndex((method) => method.id === editingMethod.id);
    const targetIndex = requestedPosition - 1;
    if (currentIndex === -1 || currentIndex === targetIndex) return;

    const reorderedMethods = [...thinkingMethods];
    const [method] = reorderedMethods.splice(currentIndex, 1);
    reorderedMethods.splice(targetIndex, 0, method);
    setLoading(true);

    try {
      const changes = reorderedMethods
        .map((item, index) => ({ item, index }))
        .filter(({ item, index }) => item.order_index !== index)
        .map(({ item, index }) =>
          supabase.from('thinking_methods').update({ order_index: index }).eq('id', item.id)
        );
      const results = await Promise.all(changes);
      const error = results.find((result) => result.error)?.error;
      if (error) throw error;

      toast({ title: "Lens moved", description: `${editingMethod.name} is now position ${requestedPosition}.` });
      setIsDialogOpen(false);
      resetForm();
      fetchThinkingMethods();
    } catch (error) {
      toast({
        title: "Could not move lens",
        description: error instanceof Error ? error.message : "The lens order could not be updated.",
        variant: "destructive"
      });
      fetchThinkingMethods();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', author: '', author_email: '', bio: '', produces: '', score: '4.5', status: 'draft', position: '', icon_file: null });
    setEditingMethod(null);
    setPreviewIconUrl(null);
  };

  const openEditDialog = (method: ThinkingMethod) => {
    setEditingMethod(method);
    setFormData({
      name: method.name, author: method.author, author_email: method.author_email || '',
      bio: method.bio, produces: method.produces ? method.produces.join(', ') : '',
      score: method.score?.toString() || '4.5', status: method.status || 'draft', position: String(thinkingMethods.findIndex((item) => item.id === method.id) + 1), icon_file: null
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => { resetForm(); setIsDialogOpen(true); };

  const fetchUnassociatedLenses = async () => {
    setLoadingLenses(true);
    try {
      const { data: uploadedLenses, error: uploadError } = await supabase.from('uploaded_lenses').select('*');
      if (uploadError) throw uploadError;
      const { data: methods, error: methodsError } = await supabase.from('thinking_methods').select('name, author_email');
      if (methodsError) throw methodsError;
      const methodNames = new Set(methods?.map(m => m.name.toLowerCase()) || []);
      const unassociated = (uploadedLenses || []).filter(lens => !methodNames.has(lens.name.toLowerCase()));
      setUnassociatedLenses(unassociated);
    } catch {
      toast({ title: "Error", description: "Failed to fetch uploaded lenses", variant: "destructive" });
    }
    setLoadingLenses(false);
  };

  const openSelectLensDialog = async () => { await fetchUnassociatedLenses(); setIsSelectLensDialogOpen(true); };

  const handleSelectLens = async (lens: UploadedLens) => {
    setIsSelectLensDialogOpen(false);
    let authorEmail = '';
    const { data: profile } = await supabase.from('profiles').select('email').eq('user_id', lens.user_id).maybeSingle();
    if (profile?.email) { authorEmail = profile.email; }
    let iconUrl: string | null = null;
    if (lens.icon_path) {
      const { data: urlData } = supabase.storage.from('expert-lens-icons').getPublicUrl(lens.icon_path);
      iconUrl = urlData?.publicUrl || null;
    }
    setFormData({
      name: lens.name, author: lens.author, author_email: authorEmail,
      bio: lens.bio || '', produces: lens.produces ? lens.produces.join(', ') : '',
      score: lens.score?.toString() || '4.5', status: 'draft', position: '', icon_file: null
    });
    setEditingMethod(null);
    setPreviewIconUrl(iconUrl);
    setIsDialogOpen(true);
  };

  // --- Access / Loading guards ---

  if (!isAdmin && !loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">You don't have admin privileges.</p>
            <Button onClick={() => navigate('/')}><ArrowLeft className="h-4 w-4 mr-2" />Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
          <p className="mt-4 text-muted-foreground">Checking admin privileges...</p>
        </div>
      </div>
    );
  }

  // --- Sidebar nav renderer ---

  const renderSidebar = () => {
    let lastGroup: string | undefined;
    return (
      <nav className="w-full lg:w-56 shrink-0 lg:border-r border-border/50 lg:pr-4 flex lg:block gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 space-y-0 lg:space-y-1">
        {NAV_ITEMS.map((item) => {
          const showGroup = item.group && item.group !== lastGroup;
          if (item.group) lastGroup = item.group;
          return (
            <React.Fragment key={item.key}>
              {showGroup && (
                <div className="pt-4 pb-1 px-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{item.group}</span>
                </div>
              )}
              <button
                onClick={() => {
                  if (item.key === 'conversations') {
                    navigate('/conversations');
                  } else {
                    setSearchParams(withAdminTab(searchParams, item.key));
                  }
                }}
                className={cn(
                  "w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                  activeSection === item.key
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            </React.Fragment>
          );
        })}
      </nav>
    );
  };

  // --- Section content ---

  const normalizedLensSearch = lensSearch.trim().toLowerCase();
  const hasLensSearch = Boolean(normalizedLensSearch);
  const filteredThinkingMethods = thinkingMethods
    .map((method, index) => ({ method, index }))
    .filter(({ method }) => (
      method.name.toLowerCase().includes(normalizedLensSearch) ||
      (method.author ?? '').toLowerCase().includes(normalizedLensSearch)
    ));

  const renderContent = () => {
    switch (activeSection) {
      case 'conversations':
        return (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Conversations</h2>
                <p className="text-muted-foreground">View and manage all user conversations</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/conversations')}>
                <MessageSquare className="h-4 w-4 mr-2" />Open Full View
              </Button>
            </div>
            <p className="text-muted-foreground">Use the full view for filtering and reading conversations.</p>
          </div>
        );

      case 'users':
        return <UserManagement />;

      case 'invites':
        return <InviteManagement />;

      case 'beta-access':
        return <BetaAccess />;

      case 'lenses':
        return (
          <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Experts</h2>
                <p className="text-muted-foreground">Manage experts and platform content</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={handleExportToExcel} disabled={loading}>
                  <Download className="h-4 w-4 mr-2" />Export to Excel
                </Button>
                <Button variant="outline" onClick={openSelectLensDialog}>
                  <FileUp className="h-4 w-4 mr-2" />Import from Uploaded Lens
                </Button>
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />Add Lens
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="max-w-xl space-y-1.5">
                <Label htmlFor="lens-search">Book or author name</Label>
                <Input
                  id="lens-search"
                  type="search"
                  value={lensSearch}
                  onChange={(event) => setLensSearch(event.target.value)}
                  placeholder="Search books or authors"
                />
              </div>
              <div className="flex min-h-8 flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                <p aria-live="polite">
                  {hasLensSearch
                    ? `Showing ${filteredThinkingMethods.length} of ${thinkingMethods.length} lenses`
                    : `${thinkingMethods.length} lenses`}
                </p>
                {hasLensSearch && <span className="text-xs">Clear searches to reorder lenses.</span>}
                {lensSearch.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setLensSearch('')}
                  >
                    Clear searches
                  </Button>
                )}
              </div>
            </div>
            <div className="grid gap-4">
              {hasLensSearch && filteredThinkingMethods.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  No matching lenses. Try different search terms or clear the searches.
                </div>
              ) : filteredThinkingMethods.map(({ method, index }) => (
                <Card key={method.id} className="border-border/50 bg-card/50">
                  <CardContent className="p-4 space-y-0">
                    <div className="flex items-center gap-4">
                      <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                      {method.icon_url && (
                        <img src={method.icon_url} alt={method.name} className="w-12 h-12 rounded-lg object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground">{method.name}</h3>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] px-1.5 py-0 capitalize border",
                              method.status === 'published' && "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
                              method.status === 'draft' && "border-yellow-500/50 bg-yellow-500/10 text-yellow-400",
                              method.status === 'admins' && "border-blue-500/50 bg-blue-500/10 text-blue-400",
                              method.status === 'author' && "border-purple-500/50 bg-purple-500/10 text-purple-400",
                            )}
                          >
                            {method.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">by {method.author}</p>
                      </div>
                      {(attributes.get(method.id)?.length ?? 0) > 0 && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => { setPropertiesLensId(method.id); setSearchParams(withAdminTab(searchParams, 'properties')); }}
                          className="text-xs text-muted-foreground gap-1"
                          title="View properties"
                        >
                          <Database className="h-3.5 w-3.5" />
                          <Badge variant="secondary" className="text-xs px-1.5 py-0">{attributes.get(method.id)?.length}</Badge>
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {(attributes.get(method.id)?.length ?? 0) > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => {
                          setExpandedLens(expandedLens === method.id ? null : method.id);
                          setSelectedAttr(null);
                        }} aria-expanded={expandedLens === method.id}>
                          {expandedLens === method.id ? 'Hide attributes' : 'View attributes'}
                        </Button>
                      )}
                      <div className="flex items-center gap-1">
                        <div className="flex flex-col">
                          <Button variant="ghost" size="sm" onClick={() => handleMoveUp(index)} disabled={index === 0 || hasLensSearch} title={hasLensSearch ? "Clear searches to reorder lenses" : undefined} aria-label={`Move ${method.name} up`} className="h-6 w-6 p-0">
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleMoveDown(index)} disabled={index === thinkingMethods.length - 1 || hasLensSearch} title={hasLensSearch ? "Clear searches to reorder lenses" : undefined} aria-label={`Move ${method.name} down`} className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(method)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(method.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {expandedLens === method.id && attributes.get(method.id) && (
                      <div className="mt-3 pt-3 border-t border-border/50">
                        <div className="flex flex-wrap gap-1 mb-2">
                          {attributes.get(method.id)!.map((attr) => (
                            <Badge
                              key={attr.attribute_name}
                              variant={selectedAttr === attr.attribute_name ? "default" : "outline"}
                              className="cursor-pointer text-xs"
                              onClick={() => setSelectedAttr(selectedAttr === attr.attribute_name ? null : attr.attribute_name)}
                            >
                              {attr.attribute_name}
                            </Badge>
                          ))}
                        </div>
                        {selectedAttr && (() => {
                          const attr = attributes.get(method.id)!.find(a => a.attribute_name === selectedAttr);
                          if (!attr) return null;
                          return (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Updated: {new Date(attr.updated_at).toLocaleString()}</p>
                              {attributeError ? <p role="alert" className="text-sm text-destructive">{attributeError}</p>
                                : selectedContent?.thinking_method_id === method.id && selectedContent.attribute_name === selectedAttr ? (
                                  <ScrollArea className="h-[300px] rounded-md border p-3">
                                    <pre className="text-xs whitespace-pre-wrap break-words font-mono text-foreground">{selectedContent.attribute_value}</pre>
                                  </ScrollArea>
                                ) : <p className="text-sm text-muted-foreground">Loading attribute...</p>}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'properties':
        return <LensProperties initialMethodId={propertiesLensId} />;

      case 'security':
        return <SecurityAlerts />;

      case 'base44':
        return <Base44Connection />;

      case 'base44-external-apps':
        return <ExternalBase44Apps />;

      case 'base44-health':
        return <ProviderHealthChecks />;

      case 'CopyService':
        return <CopyServiceAdmin />;

      case 'apps':
        return <OperatingSystems scope="admin" embedded />;

      case 'Templates':
        return <TemplateCatalogAdmin />;

      case 'credits':
        return <CreditRequests />;

      case 'wizard-promos':
        return <WizardPromos />;

      case 'guests':
        return <GuestIpUsage />;
    }
  };

  // --- Render ---

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-foreground mb-6">Admin Panel</h1>
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          {renderSidebar()}
          <div className="flex-1 min-w-0">
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Edit/Create Lens Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMethod ? 'Edit' : 'Create'} Expert Lens</DialogTitle>
            <DialogDescription>
              {editingMethod ? 'Update the expert lens details' : 'Add a new expert lens to the platform'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="author">Author</Label>
              <Input id="author" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="author_email">Author Email</Label>
              <Input id="author_email" type="email" value={formData.author_email} onChange={(e) => setFormData({ ...formData, author_email: e.target.value })} placeholder="author@example.com" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea id="bio" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} required />
            </div>
            <div className="col-span-2">
              <Label htmlFor="produces">Produces</Label>
              <Input id="produces" value={formData.produces} onChange={(e) => setFormData({ ...formData, produces: e.target.value })} placeholder="IC Briefs, Risk Matrix, 30-Day Plans" />
            </div>
            <div>
              <Label htmlFor="score">Score</Label>
              <Input id="score" type="number" step="0.1" min="0" max="5" value={formData.score} onChange={(e) => setFormData({ ...formData, score: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="admins">Admins</SelectItem>
                  <SelectItem value="author">Author</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editingMethod && (
              <div className="col-span-2 rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="position">Order position</Label>
                    <p className="text-xs text-muted-foreground">Position 1 is shown first. Moving this lens does not change its details.</p>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="w-24">
                      <Input
                        id="position"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max={thinkingMethods.length}
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        aria-describedby="position-help"
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={handleMoveToPosition} disabled={loading}>
                      Place at position
                    </Button>
                  </div>
                </div>
                <span id="position-help" className="sr-only">Enter a position from 1 to {thinkingMethods.length}, then place this lens at that position.</span>
              </div>
            )}
            <div className="col-span-2">
              <Label htmlFor="icon">Icon</Label>
              {formData.icon_file ? (
                <div className="mb-2 flex items-center gap-3">
                  <img src={URL.createObjectURL(formData.icon_file)} alt="Cropped icon" className="w-16 h-16 rounded-lg object-cover border border-border" />
                  <div>
                    <p className="text-xs text-muted-foreground">Cropped icon ready to upload</p>
                    <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => {
                      const reader = new FileReader();
                      reader.onload = () => { setCropImageSrc(reader.result as string); setCrop({ x: 0, y: 0 }); setZoom(1); };
                      reader.readAsDataURL(formData.icon_file!);
                    }}>Re-adjust</Button>
                  </div>
                </div>
              ) : editingMethod?.icon_url ? (
                <div className="mb-2 flex items-center gap-3">
                  <img src={editingMethod.icon_url} alt="Current icon" className="w-16 h-16 rounded-lg object-cover border border-border" />
                  <div>
                    <p className="text-xs text-muted-foreground">Current icon</p>
                    <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => openCropperFromUrl(editingMethod.icon_url!)}>Adjust</Button>
                  </div>
                </div>
              ) : previewIconUrl ? (
                <div className="mb-2 flex items-center gap-3">
                  <img src={previewIconUrl} alt="Preview icon" className="w-16 h-16 rounded-lg object-cover border border-border" />
                  <div>
                    <p className="text-xs text-muted-foreground">Imported icon</p>
                    <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => openCropperFromUrl(previewIconUrl!)}>Adjust</Button>
                  </div>
                </div>
              ) : null}
              <Input id="icon" type="file" accept="image/*" onChange={handleFileSelect} />
              {cropImageSrc && (
                <div className="mt-3 border border-border rounded-lg p-3">
                  <p className="text-sm font-medium mb-2">Adjust your image</p>
                  <div className="relative w-full h-64 bg-muted rounded-md overflow-hidden">
                    <Cropper
                      image={cropImageSrc}
                      crop={crop}
                      zoom={zoom}
                      minZoom={0.5}
                      maxZoom={3}
                      aspect={1}
                      restrictPosition={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Label className="text-xs whitespace-nowrap">Zoom</Label>
                    <input type="range" min={0.5} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full" />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleCropCancel}>Cancel</Button>
                    <Button type="button" size="sm" onClick={handleCropConfirm}>Apply Crop</Button>
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-2 flex gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              {editingMethod && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setIsDialogOpen(false); setPropertiesLensId(editingMethod.id); setSearchParams(withAdminTab(searchParams, 'properties')); }}
                >
                  <Database className="h-4 w-4 mr-2" />Properties
                </Button>
              )}
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : editingMethod ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Select Uploaded Lens Dialog */}
      <Dialog open={isSelectLensDialogOpen} onOpenChange={setIsSelectLensDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Import from Uploaded Lens</DialogTitle>
            <DialogDescription>Select an uploaded lens that doesn't have an associated thinking method</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 mt-4">
            {loadingLenses ? (
              <div className="text-center py-8 text-muted-foreground">Loading lenses...</div>
            ) : unassociatedLenses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No unassociated lenses found</div>
            ) : (
              unassociatedLenses.map((lens) => (
                <Card key={lens.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => handleSelectLens(lens)}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-foreground">{lens.name}</h4>
                        <p className="text-sm text-muted-foreground">by {lens.author}</p>
                        {lens.bio && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{lens.bio}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          <div className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsSelectLensDialogOpen(false)} className="w-full">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
